import { useAtom } from '@reatom/npm-react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { premiumConfigAtom } from '@root/entities/premium'
import { BuyPremiumIcon } from '@root/entities/premium/ui'

import type { DeviceType } from '@blade/ui-kit'
import type { PremiumMeta } from '@root/entities/premium'

type Props = {
  onSubscriptionInfoClick: () => void
  onBoostersInfoClick: () => void
  variant?: 'vertical' | 'horizontal'
  device?: DeviceType
}

export const CheckCurrencyProducts: React.FC<Props> = ({
  onBoostersInfoClick,
  onSubscriptionInfoClick,
  variant = 'horizontal',
  device = 'mobile',
}) => {
  const { t } = useTranslation()
  const [{ premiumBoosters, boosterPriceSol }] = useAtom(premiumConfigAtom)
  const size = PREMIUM_ICON_DEVICE_MAP[device].size
  const sizeIcon = PREMIUM_ICON_DEVICE_MAP[device].sizeIcon

  const render = (
    product: PremiumMeta.PremiumProduct,
    title: React.ReactElement,
    description: string,
  ) => {
    if (device === 'mobile' || device === 'desktop') {
      return (
        <>
          <Group>
            <BuyPremiumIcon product={product} size={size} sizeIcon={sizeIcon} />
            <Text>
              <Title $product={product} $device={device}>
                {title}
              </Title>
            </Text>
          </Group>
          <Description $device={device}>{description}</Description>
        </>
      )
    }

    return (
      <Group>
        <BuyPremiumIcon product={product} size={size} sizeIcon={sizeIcon} />
        <Text>
          <Title $product={product} $device={device}>
            {title}
          </Title>
          <Description $device={device}>{description}</Description>
        </Text>
      </Group>
    )
  }

  return (
    <Root aria-orientation={variant}>
      <Product onClick={onSubscriptionInfoClick}>
        <Glow data-product="subscription" data-device={device} />

        {render(
          'subscription',
          <Trans
            i18nKey="premium.product.premium-title"
            components={[<span key={0} />]}
          />,
          t('premium.product.premium-text', {
            boosts: premiumBoosters,
          }),
        )}
      </Product>
      <Product onClick={onBoostersInfoClick}>
        <Glow data-product="boosters" data-device={device} />
        {render(
          'boosters',
          <Trans
            i18nKey="premium.product.booster-title"
            components={[<span key={0} />]}
          />,
          t('premium.product.booster-text', {
            price: boosterPriceSol,
          }),
        )}
      </Product>
    </Root>
  )
}

const PREMIUM_ICON_DEVICE_MAP: Record<
  DeviceType,
  {
    size: number
    sizeIcon: number
  }
> = {
  mobile: {
    size: 28,
    sizeIcon: 12,
  },
  tablet: {
    size: 50,
    sizeIcon: 20,
  },
  desktop: {
    size: 28,
    sizeIcon: 12,
  },
}

const Root = styled.div`
  display: grid;

  &[aria-orientation='vertical'] {
    gap: 6px;
  }
  &[aria-orientation='horizontal'] {
    grid-template-columns: 1fr 1fr;
    grid-gap: 7px;
  }
`

const Product = styled.div`
  border: 1px solid var(--backgroundA-2);
  background: linear-gradient(
    180deg,
    rgba(var(--background-12-rgb), 0.06) 0%,
    rgba(var(--background-12-rgb), 0.02) 58%
  );

  display: grid;
  overflow: hidden;
  cursor: pointer;

  backdrop-filter: blur(100px);
  border-radius: 18px;
  padding: 14px;
  gap: 8px;
  padding-inline: 14px;
  min-height: 78px;
`

const Group = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr;

  gap: 10px;
`

const Text = styled.span`
  display: grid;
  gap: 3px;
`

const Glow = styled.span`
  &[data-device='mobile'][data-product='subscription'] {
    background-image: radial-gradient(
      50% 100px at 0 0%,
      var(--subscriptionGlow-7) 10%,
      rgba(var(--appearance-inv-1-rgb), 0.02)
    );
  }

  &[data-device='mobile'][data-product='boosters'] {
    background-image: radial-gradient(
      50% 100px at 0 0%,
      var(--boostGlow-7) 10%,
      rgba(var(--appearance-inv-1-rgb), 0.02)
    );
  }

  &:not([data-device='mobile'])[data-product='subscription'] {
    background-image: radial-gradient(
      100% 100% at 0 50%,
      var(--subscriptionGlow-7) 10%,
      rgba(var(--appearance-inv-1-rgb), 0.02)
    );
  }

  &:not([data-device='mobile'])[data-product='boosters'] {
    background-image: radial-gradient(
      100% 100% at 0 50%,
      var(--boostGlow-7) 10%,
      rgba(var(--appearance-inv-1-rgb), 0.02)
    );
  }

  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: -1;
  border-radius: 18px;
`

const Title = styled(Typography).attrs({
  variant: 'body2-semi-bold',
  component: 'h3',
})<{
  $product: PremiumMeta.PremiumProduct
  $device: DeviceType
}>`
  color: var(--text-12);
  font-style: normal;
  text-transform: uppercase;

  span {
    background: linear-gradient(
      90deg,
      ${({ $product }) =>
          $product === 'subscription'
            ? 'var(--subscriptionGradientFrom-9)'
            : 'var(--boostGradientFrom-9)'}
        50%,
      ${({ $product }) =>
          $product === 'subscription'
            ? 'var(--subscriptionGradientTo-9)'
            : 'var(--boostGradientTo-9)'}
        100%
    );

    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`

const Description = styled(Typography).attrs({
  variant: 'description-medium',
  component: 'p',
})<{ $device: DeviceType }>`
  color: var(--textA-8);

  ${({ $device }) =>
    $device !== 'mobile' &&
    `
    font-size: var(--typography-body-4-medium-font-size);
    line-height: var(--typography-body-4-medium-line-height);
    font-weight: var(--typography-body-4-medium-font-weight);
  `}
`
